import React, { useState, useEffect } from 'react';
import { useDeals } from '../context/DealsContext';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiFilter, FiX, FiCalendar, FiUser, FiDollarSign, FiList } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import DealModal from '../components/DealModal';

const Pipeline = () => {
  const { deals, deleteDeal } = useDeals();
  const [showModal, setShowModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [filterStage, setFilterStage] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('lastActivity');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState('card'); // 'card', 'table', or 'list'

  const stages = ['All', 'Discovery', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];

  // Filter deals by stage and search term
  const filteredDeals = deals
    .filter(deal => filterStage === 'All' || deal.stage === filterStage)
    .filter(deal => {
      const term = searchTerm.toLowerCase();
      return (
        deal.company?.toLowerCase().includes(term) ||
        deal.contact?.toLowerCase().includes(term) ||
        deal.notes?.toLowerCase().includes(term) ||
        deal.purchaserName?.toLowerCase().includes(term) ||
        deal.purchaserContact?.toLowerCase().includes(term) ||
        deal.purchaserName2?.toLowerCase().includes(term) ||
        deal.purchaserContact2?.toLowerCase().includes(term)
      );
    });

  // Sort deals
  const sortedDeals = [...filteredDeals].sort((a, b) => {
    let compareA, compareB;

    // Determine values to compare based on sort field
    switch (sortField) {
      case 'company':
        compareA = a.company?.toLowerCase();
        compareB = b.company?.toLowerCase();
        break;
      case 'value':
        compareA = a.value;
        compareB = b.value;
        break;
      case 'qualityScore':
        compareA = a.qualityScore;
        compareB = b.qualityScore;
        break;
      case 'probability':
        compareA = a.probability;
        compareB = b.probability;
        break;
      case 'projectedCloseDate':
        compareA = a.projectedCloseDate ? new Date(a.projectedCloseDate) : new Date(9999, 11, 31);
        compareB = b.projectedCloseDate ? new Date(b.projectedCloseDate) : new Date(9999, 11, 31);
        break;
      default: // Default sort by lastActivity
        compareA = new Date(a.lastActivity || '2000-01-01');
        compareB = new Date(b.lastActivity || '2000-01-01');
    }

    // Apply sort direction
    return sortDirection === 'asc' ? (compareA < compareB ? -1 : 1) : (compareA > compareB ? -1 : 1);
  });

  const getQualityColor = (score) => {
    if (score >= 8) return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/50';
    if (score >= 6) return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800/50';
    return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/50';
  };

  const getStageColor = (stage) => {
    const colors = {
      'Discovery': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      'Qualification': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'Proposal': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      'Negotiation': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      'Closed Won': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'Closed Lost': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingDeal(null);
    setShowModal(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Check if a deal has all required strength factors
  const isDealStrong = (deal) => {
    return deal.purchaserName && deal.purchaserContact && deal.projectedCloseDate;
  };

  // Format date as MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sales Pipeline</h1>
        <button
          onClick={handleAdd}
          className="hidden md:flex bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Deal</span>
        </button>
      </div>

      {/* Search and filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search deals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <SafeIcon icon={FiSearch} className="w-5 h-5 text-gray-400 dark:text-gray-300" />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-gray-400 dark:text-gray-300" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg transition-colors ${
              showFilters
                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700/50 text-blue-700 dark:text-blue-300'
                : 'hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-white'
            }`}
          >
            <SafeIcon icon={FiFilter} className={`w-4 h-4 ${showFilters ? 'text-blue-600 dark:text-blue-300' : 'dark:text-white'}`} />
            <span>Filters {showFilters ? '▲' : '▼'}</span>
          </button>
        </div>

        {showFilters && (
          <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stage</label>
                <div className="flex flex-wrap gap-2">
                  {stages.map(stage => (
                    <button
                      key={stage}
                      onClick={() => setFilterStage(stage)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        filterStage === stage
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {stage}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort By</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { field: 'lastActivity', label: 'Recent' },
                    { field: 'company', label: 'Company' },
                    { field: 'value', label: 'Value' },
                    { field: 'qualityScore', label: 'Quality' },
                    { field: 'probability', label: 'Probability' },
                    { field: 'projectedCloseDate', label: 'Close Date' }
                  ].map(option => (
                    <button
                      key={option.field}
                      onClick={() => handleSort(option.field)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        sortField === option.field
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {option.label} {getSortIcon(option.field)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">View Mode</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { mode: 'card', label: 'Cards' },
                    { mode: 'list', label: 'List' },
                    { mode: 'table', label: 'Table' }
                  ].map(option => (
                    <button
                      key={option.mode}
                      onClick={() => setViewMode(option.mode)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        viewMode === option.mode
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Card View */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedDeals.map((deal) => (
            <div
              key={deal.id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border ${
                isDealStrong(deal) ? 'border-green-300 dark:border-green-700' : 'border-gray-200 dark:border-gray-700'
              } p-4`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">{deal.company}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{deal.contact}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(deal)}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full transition-colors"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteDeal(deal.id)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Value</p>
                  <p className="font-medium text-gray-900 dark:text-white">${deal.value?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Quality</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getQualityColor(
                      deal.qualityScore
                    )}`}
                  >
                    {deal.qualityScore}/10
                  </span>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Stage</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(deal.stage)}`}>
                    {deal.stage}
                  </span>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Probability</p>
                  <p className="font-medium text-gray-900 dark:text-white">{deal.probability}%</p>
                </div>
              </div>

              {/* Deal Strength Indicators */}
              <div className="mt-3 flex flex-wrap gap-2">
                {deal.purchaserName && deal.purchaserContact && (
                  <div className="flex items-center text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full border border-blue-200 dark:border-blue-800/50">
                    <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                    <span>{deal.purchaserName}</span>
                  </div>
                )}
                {deal.purchaserName2 && deal.purchaserContact2 && (
                  <div className="flex items-center text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full border border-green-200 dark:border-green-800/50">
                    <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                    <span>{deal.purchaserName2}</span>
                  </div>
                )}
                {deal.projectedCloseDate && (
                  <div className="flex items-center text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full border border-purple-200 dark:border-purple-800/50">
                    <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1" />
                    <span>{formatDate(deal.projectedCloseDate)}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 flex justify-between items-center">
                <span>Updated: {formatDate(deal.lastActivity)}</span>
                <button
                  onClick={() => handleEdit(deal)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}

          {sortedDeals.length === 0 && (
            <div className="col-span-full text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400">No deals match your filters</p>
              <button
                onClick={handleAdd}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Your First Deal
              </button>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {sortedDeals.map((deal) => (
            <div
              key={deal.id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border ${
                isDealStrong(deal) ? 'border-green-300 dark:border-green-700' : 'border-gray-200 dark:border-gray-700'
              } p-3 hover:shadow-md transition-shadow`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="flex-shrink-0">
                    <span className={`inline-flex w-10 h-10 items-center justify-center rounded-full ${getStageColor(deal.stage)}`}>
                      {deal.stage.substring(0, 1)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">{deal.company}</h3>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{deal.contact}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">${deal.value?.toLocaleString()}</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">•</span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStageColor(deal.stage)}`}>
                        {deal.stage}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">•</span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border ${getQualityColor(deal.qualityScore)}`}>
                        {deal.qualityScore}/10
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-2 sm:mt-0">
                  <div className="flex flex-wrap gap-1">
                    {deal.purchaserName && deal.purchaserContact && (
                      <div className="flex items-center text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full border border-blue-200 dark:border-blue-800/50">
                        <SafeIcon icon={FiUser} className="w-3 h-3" />
                      </div>
                    )}
                    {deal.projectedCloseDate && (
                      <div className="flex items-center text-xs text-purple-700 dark:text-purple-300">
                        <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1" />
                        <span>{formatDate(deal.projectedCloseDate)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleEdit(deal)}
                      className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                    >
                      <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteDeal(deal.id)}
                      className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {sortedDeals.length === 0 && (
            <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400">No deals match your filters</p>
              <button
                onClick={handleAdd}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Your First Deal
              </button>
            </div>
          )}
        </div>
      )}

      {/* Desktop Table View */}
      {viewMode === 'table' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('company')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Company</span>
                      {sortField === 'company' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortField === 'value' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Purchasers
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('projectedCloseDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Close Date</span>
                      {sortField === 'projectedCloseDate' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('qualityScore')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Quality</span>
                      {sortField === 'qualityScore' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedDeals.map((deal) => (
                  <tr
                    key={deal.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      isDealStrong(deal) ? 'bg-green-50 dark:bg-green-900/10' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{deal.company}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{deal.contact}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      ${deal.value?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(deal.stage)}`}>
                        {deal.stage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {deal.purchaserName ? (
                          <div className="flex items-center text-xs">
                            <span className="w-4 h-4 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full flex items-center justify-center mr-1">
                              P1
                            </span>
                            <span
                              className="truncate max-w-[120px] text-gray-900 dark:text-gray-200"
                              title={`${deal.purchaserName} (${deal.purchaserContactType})`}
                            >
                              {deal.purchaserName}
                            </span>
                          </div>
                        ) : (
                          <div className="h-4"></div>
                        )}
                        {deal.purchaserName2 ? (
                          <div className="flex items-center text-xs">
                            <span className="w-4 h-4 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full flex items-center justify-center mr-1">
                              P2
                            </span>
                            <span
                              className="truncate max-w-[120px] text-gray-900 dark:text-gray-200"
                              title={`${deal.purchaserName2} (${deal.purchaserContactType2})`}
                            >
                              {deal.purchaserName2}
                            </span>
                          </div>
                        ) : (
                          <div className="h-4"></div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {deal.projectedCloseDate ? (
                        <span className="flex items-center">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1 text-purple-600 dark:text-purple-400" />
                          {formatDate(deal.projectedCloseDate)}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Not set</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getQualityColor(
                          deal.qualityScore
                        )}`}
                      >
                        {deal.qualityScore}/10
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(deal)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteDeal(deal.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sortedDeals.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No deals match your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Floating action button for mobile */}
      <div className="lg:hidden fixed bottom-6 right-6">
        <button
          onClick={handleAdd}
          className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center shadow-lg hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-6 h-6 text-white" />
        </button>
      </div>

      {showModal && <DealModal deal={editingDeal} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default Pipeline;